import React, { useEffect, useMemo, useRef, useState } from "react";

const normalizeWord = (value) => {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[.,!?;:()[\]{}"“”'`]+$/g, "");
};

const getWordScoreColor = (score, errorType) => {
  if (errorType === "Omission") return "bg-gray-400";
  if (typeof score !== "number") return "bg-gray-400";
  if (score >= 80) return "bg-green-500";
  if (score >= 40) return "bg-yellow-500";
  return "bg-red-500";
};

const getPhonemeDotColor = (score) => {
  if (typeof score !== "number") return "bg-gray-400";
  if (score >= 80) return "bg-green-500";
  if (score >= 40) return "bg-yellow-500";
  return "bg-red-500";
};

const getWordAssessmentClasses = (score, errorType) => {
  if (errorType === "Omission") {
    return "bg-gray-50 border-gray-300 text-gray-800";
  }

  if (typeof score !== "number") {
    return "bg-white/80 border-white/40 text-gray-800";
  }

  if (score >= 80) {
    return "bg-green-50 border-green-200 text-gray-800";
  }

  if (score >= 40) {
    return "bg-yellow-50 border-yellow-200 text-gray-800";
  }

  return "bg-red-50 border-red-200 text-gray-800";
};

const getActiveRingClass = (practiceMode) => {
  if (practiceMode === "shadowing") {
    return "ring-2 ring-orange-500 border-orange-300 shadow-lg shadow-orange-500/20 scale-105";
  }

  if (practiceMode === "echoing") {
    return "ring-2 ring-blue-500 border-blue-300 shadow-lg shadow-blue-500/20 scale-105";
  }

  return "ring-2 ring-emerald-500 border-emerald-300 shadow-lg shadow-emerald-500/20 scale-105";
};

const buildSegmentAssessmentMap = (segmentWords = [], assessmentWords = []) => {
  let pointer = 0;

  return segmentWords.map((item) => {
    const segmentText = normalizeWord(item?.text);

    if (!segmentText) return null;

    for (let i = pointer; i < assessmentWords.length; i++) {
      if (normalizeWord(assessmentWords[i]?.Word) === segmentText) {
        pointer = i + 1;
        return assessmentWords[i];
      }
    }

    const fallback =
      assessmentWords.find(
        (word) => normalizeWord(word?.Word) === segmentText
      ) || null;

    return fallback;
  });
};

const SentencePracticeBlock = ({
  segment,
  segments,
  assessment,
  renderHelpPill,
  renderCommentsPill,
  isMetadataNoHelp,
  setShowExerciseHelp,
  handlePreviousSegment,
  handleNextSegment,
  showCenterCue,
  practiceMode,
  echoingPhase,
  isCueActive,
  recordingStatus,
  cuePulseCount,
  echoingPulseCount,
  sentenceLabel,
  currentWord,
  handleWordPlay,
  openWordMenu,
  handleRangePlay,
  showWords = true,
  showWordArea = true,
  enableWordActions = true,
  isPlayingSegment = false,
  
}) => {
  const selectionAreaRef = useRef(null);

  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [showSelectionMenu, setShowSelectionMenu] = useState(false);

  const normalizedSelection = useMemo(() => {
    if (selectionStart == null || selectionEnd == null) return null;

    return {
      start: Math.min(selectionStart, selectionEnd),
      end: Math.max(selectionStart, selectionEnd),
    };
  }, [selectionStart, selectionEnd]);

  const clearSelection = () => {
    setIsSelecting(false);
    setSelectionStart(null);
    setSelectionEnd(null);
    setShowSelectionMenu(false);
  };

  const playSelectedRange = () => {
    if (!normalizedSelection || !segment?.words?.length) return;

    const { start, end } = normalizedSelection;

    handleRangePlay?.({
      startIndex: start,
      endIndex: end,
    });
  };

  const handleWordMouseDown = (index, e) => {
    if (!enableWordActions || practiceMode === "shadowing") return;

    e.preventDefault();
    setIsSelecting(true);
    setSelectionStart(index);
    setSelectionEnd(index);
    setShowSelectionMenu(false);
  };

  const handleWordMouseEnter = (index) => {
    if (!isSelecting) return;
    setSelectionEnd(index);
  };

  const handleWordMouseUp = () => {
    if (!isSelecting) return;

    setIsSelecting(false);

    if (
      selectionStart != null &&
      selectionEnd != null &&
      selectionStart !== selectionEnd
    ) {
      setShowSelectionMenu(true);
    }
  };

  useEffect(() => {
    clearSelection();
  }, [segment?.id]);

  useEffect(() => {
    if (!enableWordActions || !showWords) clearSelection();
  }, [enableWordActions, showWords]);

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isSelecting) {
        setIsSelecting(false);

        if (
          selectionStart != null &&
          selectionEnd != null &&
          selectionStart !== selectionEnd
        ) {
          setShowSelectionMenu(true);
        }
      }
    };

    const handleDocumentMouseDown = (e) => {
      if (!selectionAreaRef.current) return;

      if (!selectionAreaRef.current.contains(e.target)) {
        clearSelection();
      }
    };

    document.addEventListener("mouseup", handleGlobalMouseUp);
    document.addEventListener("mousedown", handleDocumentMouseDown);

    return () => {
      document.removeEventListener("mouseup", handleGlobalMouseUp);
      document.removeEventListener("mousedown", handleDocumentMouseDown);
    };
  }, [isSelecting, selectionStart, selectionEnd]);

  const mappedAssessmentWords = useMemo(() => {
    const assessmentWords = assessment?.pronunciation?.NBest?.[0]?.Words || [];
    return buildSegmentAssessmentMap(segment?.words || [], assessmentWords);
  }, [segment?.words, assessment]);

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/40 shadow-lg shadow-blue-500/5">
      <div className="px-3 sm:px-4 py-3">
        <div className="sm:hidden space-y-2">
          <div className="flex items-center">
            <div className="w-10" />

           <div className="flex items-center justify-center flex-none min-w-0 gap-2">
  {showWords ? renderHelpPill() : null}
  {showWords ? renderCommentsPill?.() : null}

</div>

            <div className="w-10 flex justify-end">
              {showWords && isMetadataNoHelp && (
                <button
                  type="button"
                  onClick={() => setShowExerciseHelp(true)}
                  className="h-9 px-4 rounded-xl bg-slate-900/5 hover:bg-slate-900/10 border border-slate-300/60 text-sm font-medium text-slate-700 backdrop-blur-sm transition-all duration-200 hover:shadow-sm"
                  title="About course"
                >
                  About
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={handlePreviousSegment}
              disabled={segment.id === 0}
              className={[
                "h-10 px-4 rounded-xl transition-all duration-200 flex items-center gap-2 shrink-0",
                segment.id === 0
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white/70 text-gray-700 hover:bg-white border border-white/40 hover:border-white/70 hover:shadow-md",
              ].join(" ")}
            >
              <span className="text-lg leading-none">←</span>
              <span className="text-sm font-medium">Prev</span>
            </button>

            <div className="relative h-6 flex items-center justify-center">
              <div
                className={[
                  "absolute inset-0 flex items-center justify-center transition-opacity duration-200",
                  showCenterCue ? "opacity-0" : "opacity-100",
                ].join(" ")}
              >
                <span className="text-sm font-semibold text-gray-700">
                  Sentence {segment.id + 1}/{segments.length}
                </span>
              </div>

              <div
                className={[
                  "absolute inset-0 flex items-center justify-center transition-opacity duration-200",
                  showCenterCue ? "opacity-100" : "opacity-0",
                ].join(" ")}
              >
                {practiceMode === "echoing" && echoingPhase === "paused" && (
                  <span className="text-sm font-semibold text-blue-700">
                    Get ready…
                  </span>
                )}

                {practiceMode === "shadowing" &&
                  (isCueActive || recordingStatus === "recording") && (
                    <span
                      className={`text-sm font-semibold transition-all duration-200 ${
                        recordingStatus === "recording"
                          ? "text-green-600 scale-105"
                          : "text-amber-700"
                      }`}
                    >
                      {recordingStatus === "recording" ? "Go!" : "Get ready…"}
                    </span>
                  )}
              </div>
            </div>

            <button
              type="button"
              onClick={handleNextSegment}
              disabled={segment.id >= segments.length - 1}
              className={[
                "h-10 px-4 rounded-xl transition-all duration-200 flex items-center gap-2 shrink-0",
                segment.id >= segments.length - 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white/70 text-gray-700 hover:bg-white border border-white/40 hover:border-white/70 hover:shadow-md",
              ].join(" ")}
            >
              <span className="text-sm font-medium">Next</span>
              <span className="text-lg leading-none">→</span>
            </button>
          </div>
        </div>

        <div className="hidden sm:flex flex-col gap-2">
          <div className="flex items-center min-h-[24px]">
            <div className="flex-1" />

           <div className="flex items-center justify-center flex-none min-w-0 gap-2">
  {showWords ? renderHelpPill() : null}

  {showWords ? renderCommentsPill?.() : null}
</div>

            <div className="flex-1 flex justify-end">
              {showWords && isMetadataNoHelp && (
                <button
                  type="button"
                  onClick={() => setShowExerciseHelp(true)}
                  className="h-10 px-4 rounded-full bg-white/70 hover:bg-white border border-indigo-200/70 shadow-[0_10px_30px_-22px_rgba(99,102,241,.8)] hover:shadow-[0_18px_50px_-28px_rgba(99,102,241,1)] transition-all duration-200 text-sm font-bold text-indigo-700 inline-flex items-center gap-2 hover:-translate-y-[1px]"
                  title="About this course"
                >
                  <span className="text-base">✨</span>
                  <span>About this course</span>
                  <span className="opacity-70">›</span>
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={handlePreviousSegment}
              disabled={segment.id === 0}
              className={[
                "h-10 px-4 rounded-xl transition-all duration-200 flex items-center gap-2",
                segment.id === 0
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white/70 text-gray-700 hover:bg-white border border-white/40 hover:border-white/70 hover:shadow-md",
              ].join(" ")}
            >
              <span className="text-lg leading-none">←</span>
              <span className="text-sm font-medium">Previous</span>
            </button>

            <div className="flex-1 min-w-0 h-10 flex items-center justify-center">
              {!showCenterCue ? (
                <div className="text-sm font-semibold text-gray-900 truncate">
                  {sentenceLabel}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span
                    className={`text-xs font-semibold transition-all duration-200 ${
                      practiceMode === "shadowing" &&
                      recordingStatus === "recording"
                        ? "text-green-600 scale-105"
                        : "text-gray-700"
                    }`}
                  >
                    {practiceMode === "shadowing" &&
                    recordingStatus === "recording"
                      ? "Go!"
                      : "Get ready…"}
                  </span>

                  {practiceMode === "shadowing" &&
                    isCueActive &&
                    recordingStatus !== "recording" && (
                      <div className="flex items-center gap-1">
                        <div
                          className={`w-2 h-2 rounded-full ${
                            cuePulseCount >= 1 ? "bg-amber-500" : "bg-gray-300"
                          }`}
                        />
                        <div className="w-3 h-0.5 bg-gray-300" />
                        <div
                          className={`w-2 h-2 rounded-full ${
                            cuePulseCount >= 2 ? "bg-orange-500" : "bg-gray-300"
                          }`}
                        />
                        <div className="w-3 h-0.5 bg-gray-300" />
                        <div
                          className={`w-2 h-2 rounded-full ${
                            cuePulseCount >= 3
                              ? "bg-green-500 animate-pulse"
                              : "bg-gray-300"
                          }`}
                        />
                      </div>
                    )}

                  {practiceMode === "echoing" &&
                    echoingPhase === "paused" && (
                      <div className="flex items-center gap-1">
                        <div
                          className={`w-2 h-2 rounded-full ${
                            echoingPulseCount >= 1
                              ? "bg-blue-500"
                              : "bg-gray-300"
                          }`}
                        />
                        <div className="w-3 h-0.5 bg-gray-300" />
                        <div
                          className={`w-2 h-2 rounded-full ${
                            echoingPulseCount >= 2
                              ? "bg-indigo-500"
                              : "bg-gray-300"
                          }`}
                        />
                        <div className="w-3 h-0.5 bg-gray-300" />
                        <div
                          className={`w-2 h-2 rounded-full ${
                            echoingPulseCount >= 3
                              ? "bg-green-500 animate-pulse"
                              : "bg-gray-300"
                          }`}
                        />
                      </div>
                    )}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={handleNextSegment}
              disabled={segment.id >= segments.length - 1}
              className={[
                "h-10 px-4 rounded-xl transition-all duration-200 flex items-center gap-2",
                segment.id >= segments.length - 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white/70 text-gray-700 hover:bg-white border border-white/40 hover:border-white/70 hover:shadow-md",
              ].join(" ")}
            >
              <span className="text-sm font-medium">Next</span>
              <span className="text-lg leading-none">→</span>
            </button>
          </div>
        </div>
      </div>

      {showWordArea && (
      <div
        ref={selectionAreaRef}
        className="relative px-4 pt-4 pb-3 bg-gradient-to-r from-blue-50/50 to-indigo-50/50 rounded-2xl border border-blue-200/30"
      >
        {enableWordActions &&
          showWords &&
          showSelectionMenu &&
          normalizedSelection &&
          practiceMode !== "shadowing" && (
            <div className="absolute left-1/2 top-3 -translate-x-1/2 z-50 pointer-events-none">
              <div className="pointer-events-auto flex items-center gap-2 px-2 py-2 rounded-2xl bg-slate-900/95 text-white shadow-2xl border border-slate-700">
                <button
                  type="button"
                  onClick={playSelectedRange}
                  className="px-3 py-1.5 rounded-xl bg-blue-500 hover:bg-blue-600 text-sm font-medium transition whitespace-nowrap"
                >
                  ▶ Play selected
                </button>

                <button
                  type="button"
                  onClick={clearSelection}
                  className="px-3 py-1.5 rounded-xl bg-white text-gray-700 hover:bg-slate-100 text-sm font-medium transition whitespace-nowrap border border-slate-300 shadow-sm"
                >
                  Clear
                </button>
              </div>
            </div>
          )}

        {showWords ? (
        <div className="flex flex-wrap gap-2 justify-center items-center">
          {segment?.words?.map((item, i) => {
            const matchedAssessment = mappedAssessmentWords[i];
            const pronunciation = matchedAssessment?.PronunciationAssessment;
            const phonemes = matchedAssessment?.Phonemes || [];
            const score = pronunciation?.AccuracyScore;
            const errorType = pronunciation?.ErrorType;

            const isActive = currentWord === i;

            const isShadowCueWord =
              practiceMode === "shadowing" &&
              isCueActive &&
              cuePulseCount >= 1 &&
              i === 0;

            const isInSelection =
              normalizedSelection &&
              i >= normalizedSelection.start &&
              i <= normalizedSelection.end;

            const isMultiSelection =
              normalizedSelection &&
              normalizedSelection.start !== normalizedSelection.end;

            const onWordClick = !enableWordActions
              ? undefined
              : practiceMode === "shadowing"
              ? () => handleWordPlay(i, item)
              : (e) => {
                  if (isMultiSelection) return;
                  openWordMenu(e, item.text, i);
                };

            const assessmentClass = getWordAssessmentClasses(score, errorType);

            const idleClass = [assessmentClass, "hover:shadow-md"].join(" ");
            const activeClass = getActiveRingClass(practiceMode);

            const selectionClass =
              "bg-gradient-to-r from-green-300 to-green-300 text-white font-semibold border border-green-500 shadow-lg shadow-green-300/30 scale-105";

            const ringClass =
              practiceMode === "manual"
                ? "ring-1 ring-emerald-200/60"
                : practiceMode === "echoing"
                ? "ring-1 ring-indigo-200/60"
                : "ring-1 ring-orange-200/60";

            return (
              <span
                key={i}
                onClick={onWordClick}
                onMouseDown={
                  enableWordActions ? (e) => handleWordMouseDown(i, e) : undefined
                }
                onMouseEnter={
                  enableWordActions ? () => handleWordMouseEnter(i) : undefined
                }
                onMouseUp={enableWordActions ? handleWordMouseUp : undefined}
                className={[
                  "text-lg sm:text-xl font-medium px-3.5 py-2.5 rounded-xl transition-all duration-300 cursor-pointer relative overflow-hidden select-none flex flex-col items-center gap-0.5 border min-w-[68px]",
                  !isInSelection ? ringClass : "",
                  isInSelection
                    ? selectionClass
                    : isActive
                    ? `${idleClass} ${activeClass}`
                    : idleClass,
                ].join(" ")}
                title={
                  enableWordActions && matchedAssessment
                    ? `${matchedAssessment?.Word || item.text}${
                        typeof score === "number" ? ` • Score: ${score}` : ""
                      }${errorType ? ` • ${errorType}` : ""}`
                    : practiceMode === "shadowing"
                    ? "Tap to play from here"
                    : "Drag to select words"
                }
              >
                <div className="flex items-center justify-center gap-1 h-2">
                  {phonemes.length > 0 ? (
                    phonemes.map((ph, idx) => (
                      <span
                        key={idx}
                        className={`w-2 h-2 rounded-full ${getPhonemeDotColor(
                          ph?.PronunciationAssessment?.AccuracyScore
                        )} ring-1 ring-white shadow-sm`}
                        title={`${ph?.Phoneme || "phoneme"}: ${
                          ph?.PronunciationAssessment?.AccuracyScore ?? "N/A"
                        }`}
                      />
                    ))
                  ) : matchedAssessment ? (
                    <span
                      className={`w-2.5 h-2.5 rounded-full ${getWordScoreColor(
                        score,
                        errorType
                      )} ring-1 ring-white shadow-sm`}
                    />
                  ) : null}
                </div>

                <span className="text-gray-800 text-center leading-tight break-words">
                  {item.text}
                </span>

                {isShadowCueWord && (
                  <div className="absolute inset-0 bg-white/20 animate-ping rounded-xl" />
                )}
              </span>
            );
          })}
        </div>
        ) : (
          <div
            className="flex min-h-[72px] flex-wrap items-center justify-center gap-2"
            aria-label="Sentence words are hidden"
          >
            {segment?.words?.map((item, i) => {
              const isActive = isPlayingSegment && currentWord === i;
              const estimatedWidth = Math.max(68, Math.min(180,
                String(item?.text || "").length * 11 + 28
              ));

              return (
                <span
                  key={i}
                  aria-hidden="true"
                  style={{ width: `${estimatedWidth}px` }}
                  className={[
                    "h-[50px] rounded-xl border transition-all duration-300",
                    "bg-white/70 border-indigo-200/70 ring-1 ring-indigo-200/60",
                    isActive
                      ? getActiveRingClass("echoing")
                      : "shadow-sm",
                  ].join(" ")}
                />
              );
            })}
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default SentencePracticeBlock;